import {Badge} from '@/components/ui/badge'
import {useI18n} from '@/i18n'
import {
    type HoldingStatusResponse,
    HoldingStatusKind,
    HoldingStatusKindBadge,
} from '@/types/holding-status'

/* eslint-disable-next-line i18next/no-literal-string -- testid construction */
const TESTID_PREFIX = 'badge-holding-status-'

interface HoldingStatusBadgeProps {
    holding: HoldingStatusResponse | undefined
    'data-testid'?: string
}

/**
 * Picker-row badge that surfaces the provenance-aware holding status returned
 * by the {@code /profile-holdings} and {@code /holdings} endpoints (issue 1043).
 *
 * <ul>
 *   <li>{@code NONE} (or {@code undefined}) renders nothing — the picker row
 *       reads as a normal, requestable target.</li>
 *   <li>{@code DIRECT_BLOCKING} renders a destructive badge — the row should
 *       also be disabled by the caller (the badge alone is presentation, not
 *       gating).</li>
 *   <li>Every {@code INDIRECT_*} kind renders a secondary "via …" badge —
 *       re-request is allowed as a durability upgrade.</li>
 * </ul>
 *
 * Reads the variant + label off the rich enum constant
 * {@link HoldingStatusKindBadge#of} so the entire enum-to-UI mapping lives in
 * one place; this component is a thin presentation wrapper.
 */
export function HoldingStatusBadge({holding, 'data-testid': testId}: HoldingStatusBadgeProps) {
    const i18n = useI18n()
    if (!holding || holding.kind === HoldingStatusKind.NONE) {
        return null
    }
    const meta = HoldingStatusKindBadge.of(holding.kind)
    if (!meta) {
        return null
    }
    return (
        <Badge variant={meta.variant} data-testid={testId ?? TESTID_PREFIX + holding.kind.toLowerCase()}>
            {meta.label(i18n)}
        </Badge>
    )
}
